import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const TermsSection = () => {
  const [activeTab, setActiveTab] = useState('terms');

  return (
    <div className="bg-card rounded-lg shadow-elevation-2 p-24">
      <h2 className="text-2xl font-heading font-bold text-foreground mb-16">
        Legal Information
      </h2>

      <p className="text-sm text-muted-foreground mb-24">
        Last updated: January 17, 2026
      </p>

      {/* Navigation Tabs */}
      <div className="flex flex-wrap gap-12 mb-24 border-b border-border pb-16">
        <button
          onClick={() => setActiveTab('terms')}
          className={`px-16 py-8 rounded-lg font-medium transition-colors ${
            activeTab === 'terms' ?'bg-primary text-primary-foreground' :'bg-muted text-foreground hover:bg-accent'
          }`}
        >
          Terms of Service
        </button>
        <button
          onClick={() => setActiveTab('privacy')}
          className={`px-16 py-8 rounded-lg font-medium transition-colors ${
            activeTab === 'privacy' ?'bg-primary text-primary-foreground' :'bg-muted text-foreground hover:bg-accent'
          }`}
        >
          Privacy Policy
        </button>
        <button
          onClick={() => setActiveTab('refund')}
          className={`px-16 py-8 rounded-lg font-medium transition-colors ${
            activeTab === 'refund' ?'bg-primary text-primary-foreground' :'bg-muted text-foreground hover:bg-accent'
          }`}
        >
          Refund Policy
        </button>
      </div>

      {/* Terms of Service */}
      {activeTab === 'terms' && (
        <div className="space-y-20">
          <div>
            <h3 className="text-xl font-semibold mb-12 flex items-center gap-8">
              <Icon name="FileText" size={24} className="text-primary" />
              Terms of Service
            </h3>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-8">Welcome to Rovyn Budget</h4>
            <p className="text-muted-foreground leading-relaxed">
              By using Rovyn Budget, you agree to these terms. We've written them in plain language to make sure you understand what you're agreeing to.
            </p>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-8">Your Account</h4>
            <p className="text-muted-foreground leading-relaxed mb-8">
              You're responsible for keeping your account secure and for all activity that happens under your account. Choose a strong password and don't share it with anyone.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              You must be at least 18 years old to use Rovyn Budget. By creating an account, you confirm that you meet this requirement.
            </p>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-8">Subscription Terms</h4>
            <p className="text-muted-foreground leading-relaxed mb-8">
              Rovyn Budget offers a <strong>Pro plan at $6 per month</strong>. This subscription gives you access to advanced features like smart alerts, payment calendar, deeper insights, and priority support.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-8">
              Your subscription automatically renews each month unless you cancel it. You can cancel anytime from your account settings, and you'll keep access until the end of your current billing period.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              We may adjust pricing with 30 days' notice. If you don't agree with the new price, you can cancel before it takes effect.
            </p>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-8">What You Can and Can't Do</h4>
            <p className="text-muted-foreground leading-relaxed mb-8">
              <strong>You can:</strong> Use Rovyn Budget to manage your personal finances, track expenses, set budgets, and gain insights into your spending.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              <strong>You can't:</strong> Share your account with others, use the service for illegal activities, attempt to hack or disrupt the platform, or scrape data from our system.
            </p>
          </div>

          <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg p-16">
            <div className="flex items-start gap-12">
              <Icon name="AlertTriangle" size={24} className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-4" />
              <div>
                <h5 className="font-semibold text-amber-900 dark:text-amber-100 mb-8">Important Disclaimer</h5>
                <p className="text-amber-800 dark:text-amber-200 leading-relaxed">
                  <strong>Rovyn Budget is not a financial advisor.</strong> We provide tools to help you track and manage your money, but we don't offer financial, investment, or legal advice. Always consult with a qualified professional for financial decisions.
                </p>
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-8">Educational Content Disclaimer</h4>
            <p className="text-muted-foreground leading-relaxed mb-8">
              Rovyn Budget provides general financial education, budgeting tips, and informational content for educational purposes only. The information provided on this website, including blog posts, tools, and insights, does not constitute financial, investment, tax, or legal advice.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-8">
              Users should not rely on this information as a substitute for professional advice tailored to their personal financial situation. Rovyn Budget does not provide personalized financial recommendations or advisory services.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              Always consider your personal circumstances and consult with a qualified financial professional before making any financial decisions.
            </p>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-8">Termination</h4>
            <p className="text-muted-foreground leading-relaxed">
              You can close your account anytime. We may suspend or terminate accounts that violate these terms or engage in harmful behavior. If we terminate your account, you'll receive a pro-rated refund for any unused subscription time.
            </p>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-8">Changes to Terms</h4>
            <p className="text-muted-foreground leading-relaxed">
              We may update these terms from time to time. We'll notify you of significant changes via email or through the app. Continuing to use Rovyn Budget after changes means you accept the new terms.
            </p>
          </div>
        </div>
      )}

      {/* Privacy Policy */}
      {activeTab === 'privacy' && (
        <div className="space-y-20">
          <div>
            <h3 className="text-xl font-semibold mb-12 flex items-center gap-8">
              <Icon name="Shield" size={24} className="text-primary" />
              Privacy Policy
            </h3>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-8">Your Privacy Matters</h4>
            <p className="text-muted-foreground leading-relaxed">
              At Rovyn Budget, we take your privacy seriously. This policy explains what information we collect, how we use it, and how we protect it.
            </p>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-8">Information We Collect</h4>
            <p className="text-muted-foreground leading-relaxed mb-8">
              <strong>Account Information:</strong> When you sign up, we collect your name, email address, and password (which we encrypt).
            </p>
            <p className="text-muted-foreground leading-relaxed mb-8">
              <strong>Financial Data:</strong> You provide transaction details, account balances, budgets, and financial goals. This data stays in your account and is never shared without your permission.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              <strong>Usage Information:</strong> We collect data about how you use the app (like which features you use most) to improve the service.
            </p>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-8">How We Use Your Information</h4>
            <ul className="space-y-8 text-muted-foreground">
              <li className="flex items-start gap-8">
                <Icon name="Check" size={18} className="text-primary flex-shrink-0 mt-4" />
                <span>To provide and improve Rovyn Budget's features</span>
              </li>
              <li className="flex items-start gap-8">
                <Icon name="Check" size={18} className="text-primary flex-shrink-0 mt-4" />
                <span>To send you important updates about your account or subscription</span>
              </li>
              <li className="flex items-start gap-8">
                <Icon name="Check" size={18} className="text-primary flex-shrink-0 mt-4" />
                <span>To provide customer support when you need help</span>
              </li>
              <li className="flex items-start gap-8">
                <Icon name="Check" size={18} className="text-primary flex-shrink-0 mt-4" />
                <span>To analyze usage patterns and make the app better</span>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-8">Data Protection & Security</h4>
            <p className="text-muted-foreground leading-relaxed mb-8">
              We use <strong>banking-level encryption</strong> to protect your data. Your financial information is encrypted both when it's stored and when it's transmitted.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              We regularly update our security measures and monitor for suspicious activity. Our team follows strict security protocols to keep your data safe.
            </p>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-8">Third-Party Services</h4>
            <p className="text-muted-foreground leading-relaxed mb-8">
              We use trusted third-party services to help run Rovyn Budget (like payment processing and cloud hosting). These partners are carefully selected and required to protect your data.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              <strong>We never sell your personal information to anyone.</strong> Your financial data is yours, and we respect that.
            </p>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-8">Your Rights</h4>
            <p className="text-muted-foreground leading-relaxed mb-8">
              You have the right to:
            </p>
            <ul className="space-y-8 text-muted-foreground">
              <li className="flex items-start gap-8">
                <Icon name="Check" size={18} className="text-primary flex-shrink-0 mt-4" />
                <span>Access all the data we have about you</span>
              </li>
              <li className="flex items-start gap-8">
                <Icon name="Check" size={18} className="text-primary flex-shrink-0 mt-4" />
                <span>Request corrections to your data</span>
              </li>
              <li className="flex items-start gap-8">
                <Icon name="Check" size={18} className="text-primary flex-shrink-0 mt-4" />
                <span>Delete your account and all associated data</span>
              </li>
              <li className="flex items-start gap-8">
                <Icon name="Check" size={18} className="text-primary flex-shrink-0 mt-4" />
                <span>Export your data in a portable format</span>
              </li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-12">
              To exercise these rights, contact us at{' '}
              <a href="mailto:privacy@rovynbudget.com" className="text-primary hover:underline">
                privacy@rovynbudget.com
              </a>
              .
            </p>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-8">Cookies & Tracking</h4>
            <p className="text-muted-foreground leading-relaxed">
              We use cookies to keep you logged in and remember your preferences. You can control cookie settings in your browser, but some features may not work properly without them.
            </p>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-8">Changes to Privacy Policy</h4>
            <p className="text-muted-foreground leading-relaxed">
              If we make significant changes to this policy, we'll notify you via email and give you time to review the updates before they take effect.
            </p>
          </div>
        </div>
      )}

      {/* Refund Policy */}
      {activeTab === 'refund' && (
        <div className="space-y-20">
          <div>
            <h3 className="text-xl font-semibold mb-12 flex items-center gap-8">
              <Icon name="DollarSign" size={24} className="text-primary" />
              Refund Policy
            </h3>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-8">Subscription Refunds</h4>
            <p className="text-muted-foreground leading-relaxed mb-8">
              Rovyn Budget operates on a <strong>non-refundable subscription model</strong>. Once you're charged for a billing period, we don't offer refunds for that period.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              This policy helps us maintain affordable pricing and continue improving the service for everyone.
            </p>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-8">Cancellation Anytime</h4>
            <p className="text-muted-foreground leading-relaxed mb-8">
              While we don't offer refunds, <strong>you can cancel your subscription anytime</strong> from your account settings.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-8">When you cancel:</p>
            <ul className="space-y-8 text-muted-foreground">
              <li className="flex items-start gap-8">
                <Icon name="Check" size={18} className="text-primary flex-shrink-0 mt-4" />
                <span>You'll keep access to Pro features until the end of your current billing period</span>
              </li>
              <li className="flex items-start gap-8">
                <Icon name="Check" size={18} className="text-primary flex-shrink-0 mt-4" />
                <span>You won't be charged again</span>
              </li>
              <li className="flex items-start gap-8">
                <Icon name="Check" size={18} className="text-primary flex-shrink-0 mt-4" />
                <span>Your account will automatically switch to the free plan</span>
              </li>
              <li className="flex items-start gap-8">
                <Icon name="Check" size={18} className="text-primary flex-shrink-0 mt-4" />
                <span>All your data remains safe and accessible</span>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-8">Exceptional Circumstances</h4>
            <p className="text-muted-foreground leading-relaxed mb-8">
              We may consider refunds in rare cases, such as:
            </p>
            <ul className="space-y-8 text-muted-foreground">
              <li className="flex items-start gap-8">
                <Icon name="AlertCircle" size={18} className="text-amber-500 flex-shrink-0 mt-4" />
                <span>Technical issues that prevented you from using the service</span>
              </li>
              <li className="flex items-start gap-8">
                <Icon name="AlertCircle" size={18} className="text-amber-500 flex-shrink-0 mt-4" />
                <span>Billing errors or duplicate charges</span>
              </li>
              <li className="flex items-start gap-8">
                <Icon name="AlertCircle" size={18} className="text-amber-500 flex-shrink-0 mt-4" />
                <span>Unauthorized charges on your account</span>
              </li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-12">
              If you believe you qualify for an exception, contact our support team at{' '}
              <a href="mailto:support@rovynbudget.com" className="text-primary hover:underline">
                support@rovynbudget.com
              </a>{' '}
              within 7 days of the charge.
            </p>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-8">Billing Disputes</h4>
            <p className="text-muted-foreground leading-relaxed">
              If you see a charge you don't recognize or believe is incorrect, please contact us immediately. We'll investigate and resolve the issue as quickly as possible.
            </p>
          </div>

          <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-16">
            <div className="flex items-start gap-12">
              <Icon name="Info" size={24} className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-4" />
              <div>
                <h5 className="font-semibold text-blue-900 dark:text-blue-100 mb-8">Try Before You Commit</h5>
                <p className="text-blue-800 dark:text-blue-200 leading-relaxed">
                  Not sure if Pro is right for you? Start with our free plan to explore Rovyn Budget's core features. You can upgrade to Pro anytime when you're ready for advanced tools.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Contact Section */}
      <div className="mt-32 bg-accent rounded-lg p-20 text-center">
        <h4 className="text-lg font-heading font-bold mb-12">Questions About Our Policies?</h4>
        <p className="text-muted-foreground mb-16">
          We're here to help. If you have any questions about these terms, privacy practices, or refund policies, please don't hesitate to reach out.
        </p>
        <a
          href="mailto:legal@rovynbudget.com"
          className="inline-flex items-center gap-8 bg-primary text-primary-foreground px-20 py-12 rounded-lg font-medium hover:bg-primary/90 transition-colors"
        >
          <Icon name="Mail" size={18} />
          Contact Legal Team
        </a>
      </div>
    </div>
  );
};

export default TermsSection;